import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, RefreshCw, TrendingUp, Users, DollarSign, Target, ChevronRight, Phone, Building2, Send, Settings, CheckCircle, AlertCircle, Copy } from 'lucide-react';

const STAGES = [
  { id: 'contactado', label: 'Contactado', color: '#64748b', bg: '#f1f5f9' },
  { id: 'respondio',  label: 'Respondió',  color: '#3b82f6', bg: '#eff6ff' },
  { id: 'llamada',    label: 'Llamada',    color: '#8b5cf6', bg: '#f5f3ff' },
  { id: 'propuesta',  label: 'Propuesta',  color: '#f59e0b', bg: '#fffbeb' },
  { id: 'contrato',   label: 'Contrato',   color: '#10b981', bg: '#ecfdf5' },
  { id: 'pagado',     label: 'Pagado',     color: '#22c55e', bg: '#f0fdf4' },
];

const TEMPLATE_LABELS = {
  bienvenida_form: 'Bienvenida (formulario web)',
  bienvenida_directa: 'Bienvenida (WhatsApp directo)',
  seguimiento_24h: 'Seguimiento 24h',
};

export default function PeruPipelineHub({ authFetch }) {
  const [leads, setLeads]           = useState([]);
  const [report, setReport]         = useState(null);
  const [autoConfig, setAutoConfig] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [saving, setSaving]         = useState(false);
  const [view, setView]             = useState('pipeline');
  const [noteInput, setNoteInput]   = useState('');
  const [activeTab, setActiveTab]   = useState('pipeline'); // 'pipeline' | 'autorespuesta'
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateText, setTemplateText]       = useState('');
  const [waMensaje, setWaMensaje]             = useState('');
  const [waSending, setWaSending]             = useState(false);
  const [copied, setCopied]                   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, reportRes, autoRes] = await Promise.all([
        authFetch('/api/admin/peru/leads'),
        authFetch('/api/admin/peru/report'),
        authFetch('/api/admin/peru/autorespuesta'),
      ]);
      setLeads(leadsRes.leads || []);
      setReport(reportRes);
      setAutoConfig(autoRes);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const moveStage = async (lead, newStage) => {
    setSaving(true);
    try {
      await authFetch(`/api/admin/peru/leads/${lead.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: newStage }),
      });
      setLeads((prev) => prev.map((l) => l.id === lead.id ? { ...l, stage: newStage } : l));
      if (selected?.id === lead.id) setSelected({ ...lead, stage: newStage });
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async () => {
    if (!selected || !noteInput.trim()) return;
    setSaving(true);
    try {
      await authFetch(`/api/admin/peru/leads/${selected.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notas: noteInput }),
      });
      setLeads((prev) => prev.map((l) => l.id === selected.id ? { ...l, notas: noteInput } : l));
      setSelected((s) => ({ ...s, notas: noteInput }));
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async () => {
    if (!editingTemplate || !templateText.trim()) return;
    setSaving(true);
    try {
      await authFetch('/api/admin/peru/autorespuesta', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [editingTemplate]: templateText }),
      });
      setAutoConfig((prev) => ({ ...prev, config: { ...prev.config, [editingTemplate]: templateText } }));
      setEditingTemplate(null);
    } finally {
      setSaving(false);
    }
  };

  const sendWa = async (leadId, template = null) => {
    setWaSending(true);
    try {
      const body = template ? { template } : { mensaje: waMensaje };
      const res = await authFetch(`/api/admin/peru/leads/${leadId}/wa-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.success) { setWaMensaje(''); alert(`✅ ${res.message}`); }
    } catch (e) { alert('❌ No se pudo enviar. Verifica que WHATSAPP_ACCESS_TOKEN esté configurado.'); }
    finally { setWaSending(false); }
  };

  const copyText = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const byStage = (stageId) => leads.filter((l) => l.stage === stageId);
  const waLink = (lead) => `https://wa.me/${lead.whatsapp?.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${lead.nombre}, soy Juan Carlos de Neuralia369 👋`)}`;

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-text-muted">
      <RefreshCw size={20} className="animate-spin mr-2" /> Cargando pipeline...
    </div>
  );

  const waStatus = autoConfig?.whatsapp_status;
  const isWaReady = waStatus?.configured;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-text-primary">Pipeline Peru · 30 Días</h2>
          <p className="text-sm text-text-muted">Campaña activa · Aynara prospección directa</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="rounded-xl border border-border-subtle bg-card px-3 py-2 hover:border-primary/40 transition">
            <RefreshCw size={14} className="text-text-muted" />
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-border-subtle pb-2">
        {[['pipeline', 'Pipeline CRM'], ['autorespuesta', 'Auto-Respuestas WA']].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`rounded-xl px-4 py-2 text-xs font-bold transition ${activeTab === id ? 'bg-primary text-[#0A1A3F]' : 'text-text-muted hover:text-text-primary'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── TAB PIPELINE ── */}
      {activeTab === 'pipeline' && (
        <>
          {/* KPIs */}
          {report && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { icon: Users,      label: 'Total leads',   val: report.total_leads ?? 0,               color: '#3b82f6' },
                { icon: Target,     label: 'Propuestas',    val: report.by_stage?.propuesta ?? 0,       color: '#f59e0b' },
                { icon: DollarSign, label: 'Pagados',       val: report.pagados ?? 0,                   color: '#22c55e' },
                { icon: TrendingUp, label: 'Conversión',    val: `${report.conversion_rate_pct ?? 0}%`, color: '#a78bfa' },
              ].map(({ icon: Icon, label, val, color }) => (
                <div key={label} className="rounded-2xl border border-border-subtle bg-card p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={14} style={{ color }} />
                    <span className="text-xs text-text-muted">{label}</span>
                  </div>
                  <p className="text-2xl font-black text-text-primary">{val}</p>
                </div>
              ))}
            </div>
          )}

          {/* Vista toggle */}
          <div className="flex gap-2">
            {['pipeline', 'list'].map((v) => (
              <button key={v} onClick={() => setView(v)}
                className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${view === v ? 'bg-primary text-[#0A1A3F]' : 'border border-border-subtle text-text-muted hover:border-primary/40'}`}>
                {v === 'pipeline' ? 'Kanban' : 'Lista'}
              </button>
            ))}
          </div>

          {/* Kanban */}
          {view === 'pipeline' && (
            <div className="overflow-x-auto pb-2">
              <div className="flex gap-3 min-w-max">
                {STAGES.map((stage) => {
                  const stageLeads = byStage(stage.id);
                  return (
                    <div key={stage.id} className="w-52 rounded-2xl border border-border-subtle bg-card flex-shrink-0 overflow-hidden">
                      <div className="px-3 py-2.5 flex items-center justify-between" style={{ background: stage.bg, borderBottom: `2px solid ${stage.color}20` }}>
                        <span className="text-xs font-bold" style={{ color: stage.color }}>{stage.label}</span>
                        <span className="rounded-full px-2 py-0.5 text-xs font-black text-white" style={{ background: stage.color }}>{stageLeads.length}</span>
                      </div>
                      <div className="p-2 space-y-2 min-h-[100px] max-h-[380px] overflow-y-auto">
                        {stageLeads.map((lead) => (
                          <div key={lead.id} onClick={() => { setSelected(lead); setNoteInput(lead.notas || ''); setWaMensaje(''); }}
                            className="cursor-pointer rounded-xl border border-border-subtle bg-background p-3 hover:border-primary/30 transition hover:shadow-sm"
                            data-testid={`peru-lead-card-${lead.id}`}>
                            <p className="text-xs font-bold text-text-primary truncate">{lead.nombre}</p>
                            {lead.empresa && <p className="text-[11px] text-text-muted truncate">{lead.empresa}</p>}
                            <div className="mt-1.5 flex items-center justify-between">
                              <span className="text-[10px] text-text-muted">{lead.sector || '—'}</span>
                              <div className="flex gap-1">
                                {lead.autorespuesta_enviada && <CheckCircle size={10} className="text-green-500" title="Auto-respuesta enviada" />}
                                <a href={waLink(lead)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                                  className="rounded-full p-0.5 hover:bg-green-100 transition">
                                  <MessageCircle size={11} className="text-green-600" />
                                </a>
                              </div>
                            </div>
                          </div>
                        ))}
                        {stageLeads.length === 0 && <p className="px-2 py-4 text-center text-[11px] text-text-muted">Sin leads</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Lista */}
          {view === 'list' && (
            <div className="rounded-2xl border border-border-subtle bg-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle bg-background">
                    {['Nombre', 'WA', 'Sector', 'Etapa', 'WA Enviado', 'Acción'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => {
                    const stage = STAGES.find((s) => s.id === lead.stage) || STAGES[0];
                    return (
                      <tr key={lead.id} className="border-b border-border-subtle hover:bg-background/60 transition cursor-pointer"
                        onClick={() => { setSelected(lead); setNoteInput(lead.notas || ''); }}>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-text-primary text-xs">{lead.nombre}</p>
                          {lead.empresa && <p className="text-[11px] text-text-muted">{lead.empresa}</p>}
                        </td>
                        <td className="px-4 py-3 text-xs">{lead.whatsapp}</td>
                        <td className="px-4 py-3 text-xs text-text-muted">{lead.sector || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: stage.color }}>{stage.label}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {lead.autorespuesta_enviada ? <CheckCircle size={14} className="text-green-500 mx-auto" /> : <span className="text-text-muted text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <a href={waLink(lead)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 rounded-full bg-green-600 px-3 py-1 text-[11px] font-bold text-white hover:bg-green-700 transition">
                            <MessageCircle size={10} /> WA
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                  {leads.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-text-muted text-sm">Sin leads · Comparte /activa-peru</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Scripts PNL/VAK */}
          <div className="rounded-2xl border border-border-subtle bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-text-muted mb-3">Scripts PNL/VAK</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { tipo: 'Visual', key: 'vis', frase: '¿Ya viste lo que están haciendo las empresas en Lima con IA? Te muestro en 2 minutos.' },
                { tipo: 'Auditivo', key: 'aud', frase: 'Me dijeron que tu empresa está creciendo. ¿Tienes 5 min para que te cuente cómo automatizar tus ventas?' },
                { tipo: 'Kinestésico', key: 'kin', frase: 'Tengo algo concreto para ti: 7 días de prueba gratis donde conectamos Aynara a tu WhatsApp.' },
              ].map(({ tipo, key, frase }) => (
                <div key={tipo} className="rounded-xl border border-border-subtle bg-background p-3">
                  <p className="text-xs font-bold text-text-primary mb-1">{tipo}</p>
                  <p className="text-[11px] text-text-muted italic mb-2">"{frase}"</p>
                  <button onClick={() => copyText(frase, key)}
                    className="flex items-center gap-1 text-[10px] font-semibold text-primary hover:underline">
                    {copied === key ? <CheckCircle size={10} className="text-green-500" /> : <Copy size={10} />}
                    {copied === key ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Landing link */}
          <div className="rounded-2xl border border-border-subtle bg-card p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-text-primary">Landing Peru</p>
              <p className="text-xs text-text-muted">Comparte este enlace para capturar leads</p>
            </div>
            <a href="/activa-peru" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-bold text-[#0A1A3F] hover:opacity-90 transition"
              data-testid="peru-pipeline-landing-link">
              /activa-peru <ChevronRight size={12} />
            </a>
          </div>
        </>
      )}

      {/* ── TAB AUTO-RESPUESTAS ── */}
      {activeTab === 'autorespuesta' && autoConfig && (
        <div className="space-y-5">
          {/* Estado WhatsApp API */}
          <div className={`rounded-2xl border p-4 ${isWaReady ? 'border-green-500/30 bg-green-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
            <div className="flex items-center gap-2 mb-2">
              {isWaReady ? <CheckCircle size={16} className="text-green-500" /> : <AlertCircle size={16} className="text-amber-500" />}
              <p className={`text-sm font-bold ${isWaReady ? 'text-green-400' : 'text-amber-400'}`}>
                WhatsApp API: {isWaReady ? 'ACTIVO — Auto-respuestas funcionando' : 'Pendiente de activar'}
              </p>
            </div>
            {!isWaReady && (
              <div className="text-xs text-text-muted space-y-1 mt-2">
                <p className="font-semibold text-amber-300">Pendiente agregar en backend/.env:</p>
                {(waStatus?.missing_credentials || []).map((c) => (
                  <p key={c} className="pl-3">• <code className="text-amber-200">{c}</code></p>
                ))}
                <p className="mt-2 text-text-muted">👉 Ve a <strong>business.facebook.com</strong> → WhatsApp → Panel de API → Generar Token Permanente. Luego añade <code>WHATSAPP_ACCESS_TOKEN=...</code> en el .env y reinicia el backend.</p>
                <p className="mt-1">Webhook URL a registrar en Meta: <code className="text-cyan-300">{autoConfig.webhook_url}</code></p>
                <p>Verify Token: <code className="text-cyan-300">{autoConfig.verify_token}</code></p>
              </div>
            )}
          </div>

          {/* Plantillas editables */}
          <div className="space-y-4">
            <p className="text-sm font-bold text-text-primary">Plantillas de auto-respuesta</p>
            <p className="text-xs text-text-muted">Variables disponibles: <code className="text-cyan-300">&#123;nombre&#125;</code> <code className="text-cyan-300">&#123;empresa&#125;</code></p>
            {Object.entries(autoConfig.config || {}).map(([key, value]) => (
              <div key={key} className="rounded-2xl border border-border-subtle bg-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-text-primary">{TEMPLATE_LABELS[key] || key}</p>
                  {editingTemplate !== key ? (
                    <button onClick={() => { setEditingTemplate(key); setTemplateText(value); }}
                      className="rounded-xl border border-border-subtle px-3 py-1 text-xs text-text-muted hover:border-primary/40 transition">
                      Editar
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => setEditingTemplate(null)} className="text-xs text-text-muted hover:text-text-primary">Cancelar</button>
                      <button onClick={saveTemplate} disabled={saving}
                        className="rounded-xl bg-primary px-3 py-1 text-xs font-bold text-[#0A1A3F] disabled:opacity-50">
                        {saving ? '...' : 'Guardar'}
                      </button>
                    </div>
                  )}
                </div>
                {editingTemplate === key ? (
                  <textarea
                    className="w-full rounded-xl border border-primary bg-background px-3 py-2 text-xs text-text-primary focus:outline-none min-h-[120px]"
                    value={templateText}
                    onChange={(e) => setTemplateText(e.target.value)}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-xs text-text-muted bg-background rounded-xl p-3 border border-border-subtle">{value}</pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── MODAL DETALLE LEAD ── */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="peru-lead-detail-modal">
          <div className="w-full max-w-md rounded-3xl border border-border-subtle bg-card p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-black text-text-primary">{selected.nombre}</h3>
                {selected.empresa && <p className="text-sm text-text-muted">{selected.empresa}</p>}
              </div>
              <button onClick={() => setSelected(null)} className="rounded-full border border-border-subtle p-2 text-text-muted hover:text-text-primary transition">✕</button>
            </div>

            <div className="space-y-2 text-sm mb-4">
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-text-muted" />
                <a href={waLink(selected)} target="_blank" rel="noopener noreferrer" className="text-green-500 font-semibold hover:underline">{selected.whatsapp}</a>
              </div>
              {selected.sector && <div className="flex items-center gap-2"><Building2 size={14} className="text-text-muted" /><span className="text-text-muted text-xs">{selected.sector}</span></div>}
              {selected.mensaje && <div className="rounded-xl border border-border-subtle bg-background p-3 text-xs text-text-muted">"{selected.mensaje}"</div>}
            </div>

            {/* Mover etapa */}
            <div className="mb-4">
              <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Etapa</p>
              <div className="flex flex-wrap gap-1.5">
                {STAGES.map((s) => (
                  <button key={s.id} disabled={selected.stage === s.id || saving}
                    onClick={() => moveStage(selected, s.id)}
                    className="rounded-full px-3 py-1 text-[11px] font-bold text-white transition hover:opacity-80 disabled:opacity-40"
                    style={{ background: s.color }}>
                    {selected.stage === s.id ? '✓ ' : ''}{s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Enviar WA */}
            <div className="mb-4 rounded-xl border border-border-subtle bg-background p-3">
              <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Enviar WhatsApp</p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {Object.entries(TEMPLATE_LABELS).map(([key, label]) => (
                  <button key={key} onClick={() => sendWa(selected.id, key)} disabled={waSending || !isWaReady}
                    className="rounded-full border border-border-subtle px-2.5 py-1 text-[10px] font-semibold text-text-muted hover:border-green-500 hover:text-green-400 transition disabled:opacity-40">
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={waMensaje} onChange={(e) => setWaMensaje(e.target.value)}
                  placeholder="O escribe un mensaje personalizado..."
                  className="flex-1 rounded-xl border border-border-subtle bg-card px-3 py-2 text-xs text-text-primary focus:border-primary focus:outline-none" />
                <button onClick={() => sendWa(selected.id)} disabled={waSending || !waMensaje.trim() || !isWaReady}
                  className="rounded-xl bg-green-600 px-3 py-2 disabled:opacity-40 hover:bg-green-700 transition">
                  <Send size={14} className="text-white" />
                </button>
              </div>
              {!isWaReady && <p className="mt-1 text-[10px] text-amber-400">⚠ WhatsApp API no configurado — ve a la pestaña Auto-Respuestas</p>}
            </div>

            {/* Notas */}
            <div className="mb-4">
              <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Notas</p>
              <textarea className="w-full rounded-xl border border-border-subtle bg-background px-3 py-2 text-xs focus:border-primary focus:outline-none min-h-[60px]"
                placeholder="Interesado en Plan Diagnóstico, llamar el jueves..."
                value={noteInput} onChange={(e) => setNoteInput(e.target.value)} />
              <button onClick={saveNote} disabled={saving}
                className="mt-2 rounded-xl bg-primary px-4 py-2 text-xs font-bold text-[#0A1A3F] hover:opacity-90 disabled:opacity-50">
                {saving ? '...' : 'Guardar'}
              </button>
            </div>

            <a href={waLink(selected)} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-full bg-green-600 py-3 text-sm font-bold text-white hover:bg-green-700 transition">
              <MessageCircle size={16} /> Abrir WhatsApp
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
